import { useCallback, useRef } from "react";
import { useWidgetStore } from "../store";
import type { ConversationItem, Suggestion } from "../store/types";
import { normalizeActionUrl } from "../utils/normalizeActionUrl";

// Maximum time we wait for GPS permission to settle before starting the agent
// session. Kept aligned with the warmup internal timeout below.
export const GPS_WARMUP_CAP_MS = 3000;

const WIDGET_REDIRECT_MAP: Record<string, string> = {
  events: "https://remstal.de/events/kalender",
  tours: "https://remstal.de/besuchen/tourenfinder",
  pois: "https://remstal.de/staedteundgemeinden",
  views: "https://remstal.de/themen/natur/aussicht#/article",
  kafka_book:
    "https://www.klett-cotta.de/produkt/kafkas-kochbuch-9783608966657-t-9124",
};

type OpenUrlPayload = {
  url?: unknown;
  link?: unknown;
  href?: unknown;
  action_url?: unknown;
  bookingUrl?: unknown;
  title?: unknown;
  description?: unknown;
  action_label?: unknown;
  label?: unknown;
  type?: unknown;
  open_immediately?: unknown;
  openImmediately?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function getNonEmptyString(value: unknown): string | undefined {
  const stringValue = getString(value)?.trim();
  return stringValue ? stringValue : undefined;
}

function getSuggestionType(value: unknown): Suggestion["type"] {
  const type = getString(value);
  if (
    type === "location" ||
    type === "event" ||
    type === "product" ||
    type === "link" ||
    type === "info" ||
    type === "booking"
  ) {
    return type;
  }
  return "booking";
}

export const useClientTools = (debug: boolean = false, gpsEnabled: boolean = false) => {
  const gpsEnabledRef = useRef(gpsEnabled);
  gpsEnabledRef.current = gpsEnabled;
  const log = useCallback(
    (...args: unknown[]) => {
      if (debug) {
        console.log("[RemstalVoice:useClientTools]", ...args);
      }
    },
    [debug]
  );

  const handleOpenWidget = useCallback(
    async (widgetData: unknown) => {
      const type = isRecord(widgetData) ? getString(widgetData.type) : undefined;
      log("🔧 Open widget called with type:", type);

      if (!type) {
        log("⚠️ Missing widget type");
        return;
      }
      const url = WIDGET_REDIRECT_MAP[type];

      if (!url) {
        log("⚠️ Unknown widget type:", type);
        return;
      }

      log("🔗 Opening URL in new tab:", url);
      window.open(url, "_blank");
    },
    [log]
  );

  const handleOpenUrl = useCallback(
    async (linkData: unknown): Promise<string> => {
      const payload: OpenUrlPayload = isRecord(linkData) ? linkData : {};
      const rawUrl =
        payload.url ||
        payload.link ||
        payload.href ||
        payload.action_url ||
        payload.bookingUrl;
      const title =
        getNonEmptyString(payload.title) || "Booking link";
      const description =
        getNonEmptyString(payload.description) || "Open the prepared booking page.";
      const actionLabel =
        getNonEmptyString(payload.action_label) ||
        getNonEmptyString(payload.label) ||
        "Open link";

      const normalized = normalizeActionUrl(rawUrl);
      if (!normalized.ok) {
        const messageByReason = {
          missing_url: "No URL was provided to the client tool.",
          invalid_url: "The provided URL is not valid.",
          unsupported_url_protocol: "Only http and https URLs can be opened.",
        } as const;
        return JSON.stringify({
          success: false,
          error: normalized.reason,
          message: messageByReason[normalized.reason],
        });
      }
      const finalUrl = normalized.url;

      const now = Date.now();
      const suggestion: Suggestion = {
        type: getSuggestionType(payload.type),
        title,
        description,
        action_url: finalUrl,
        action_label: actionLabel,
        id: now.toString(),
      };
      const conversationItem: ConversationItem = {
        type: "suggestion",
        id: `${now}-url-tool`,
        timestamp: now,
        suggestion,
      };
      useWidgetStore.getState().addConversationItem(conversationItem);

      let opened = false;
      if (payload.open_immediately === true || payload.openImmediately === true) {
        const openedWindow = window.open(finalUrl, "_blank", "noopener,noreferrer");
        opened = openedWindow !== null;
      }

      return JSON.stringify({
        success: true,
        opened,
        displayed: true,
        message:
          "The link is available as a clickable card in the chat. Do not read the URL aloud.",
      });
    },
    []
  );

  // GPS client tool — agent calls this to get user's current location
  // Uses fallback strategy: cached → high accuracy → low accuracy
  const handleGetCurrentLocation = useCallback(
    async (_data: unknown): Promise<string> => {
      log("GPS: get_current_location called by agent");

      if (!gpsEnabledRef.current) {
        return JSON.stringify({ error: "gps_disabled" });
      }

      if (!navigator.geolocation) {
        return JSON.stringify({ error: "Geolocation not supported" });
      }

      const getPosition = (
        highAccuracy: boolean,
        timeout: number,
        maximumAge: number
      ): Promise<GeolocationPosition | null> =>
        new Promise((resolve) => {
          let settled = false;
          const settle = (position: GeolocationPosition | null): void => {
            if (settled) return;
            settled = true;
            clearTimeout(watchdog);
            resolve(position);
          };
          const watchdog = setTimeout(() => {
            log("GPS: browser callback watchdog expired", { highAccuracy, timeout });
            settle(null);
          }, timeout);

          try {
            navigator.geolocation.getCurrentPosition(
              (pos) => settle(pos),
              (err) => {
                log("GPS: attempt failed", { highAccuracy, timeout, error: err.message });
                settle(null);
              },
              { enableHighAccuracy: highAccuracy, timeout, maximumAge }
            );
          } catch (error) {
            log("GPS: browser request threw", { highAccuracy, timeout, error });
            settle(null);
          }
        });

      // 1. Try cached position first (instant, accept up to 60s old)
      let position = await getPosition(false, 3000, 60000);
      if (!gpsEnabledRef.current) {
        return JSON.stringify({ error: "gps_disabled" });
      }

      // 2. High accuracy with 5s timeout
      if (!position) {
        position = await getPosition(true, 5000, 0);
        if (!gpsEnabledRef.current) {
          return JSON.stringify({ error: "gps_disabled" });
        }
      }

      // 3. Fallback: low accuracy with 5s timeout
      if (!position) {
        log("GPS: high accuracy failed, trying low accuracy fallback");
        position = await getPosition(false, 5000, 0);
        if (!gpsEnabledRef.current) {
          return JSON.stringify({ error: "gps_disabled" });
        }
      }

      if (position) {
        const result = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: Math.round(position.coords.accuracy),
        };
        log("GPS: returning location", result);
        return JSON.stringify(result);
      }

      log("GPS: all attempts failed, returning unavailable");
      return JSON.stringify({ error: "gps_unavailable" });
    },
    [log]
  );

  // Pre-warm GPS: trigger browser permission prompt and cache a position
  // Returns a Promise that resolves when permission is decided (granted or denied)
  // or after the timeout — callers can Promise.race this against a cap before startSession
  const warmupGps = useCallback((): Promise<void> => {
    if (!navigator.geolocation) return Promise.resolve();
    log("GPS: warming up — requesting permission");
    return new Promise<void>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => {
          log("GPS: warmup position cached");
          resolve();
        },
        () => {
          log("GPS: warmup failed (ok — permission may be pending)");
          resolve();
        },
        { enableHighAccuracy: false, timeout: GPS_WARMUP_CAP_MS, maximumAge: Infinity }
      );
    });
  }, [log]);

  return { handleOpenWidget, handleOpenUrl, handleGetCurrentLocation, warmupGps };
};
